import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  InputLabel,
  Avatar,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import {
  Close,
  Group,
  Person,
  Assignment,
  CheckCircle,
  Error,
} from '@material-ui/icons';
import { useQuery } from 'react-query';
import usePortalAuth from '../../../auth/usePortalAuth';
import { postApiData, fetchApiData } from '../../../utils/service';
import basePCUrl from '../../../Globals';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      maxWidth: 600,
      width: '100%',
    },
  },
  header: {
    background: 'linear-gradient(135deg, #0055A2 0%, #428BC9 100%)',
    color: 'white',
    padding: theme.spacing(3),
    borderRadius: '16px 16px 0 0',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -20,
      right: -20,
      width: 80,
      height: 80,
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '50%',
    },
  },
  moduleInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  thumbnail: {
    width: 80,
    height: 80,
    borderRadius: 12,
    border: '2px solid rgba(255,255,255,0.3)',
  },
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: 'white',
  },
  content: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#1a237e',
  },
  radioGroup: {
    marginBottom: theme.spacing(3),
  },
  userSelect: {
    marginBottom: theme.spacing(2),
  },
  selectedUsers: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 16,
  },
  assignButton: {
    background: 'linear-gradient(45deg, #0055A2, #428BC9)',
    color: 'white',
    fontWeight: 600,
    padding: '12px 32px',
    '&:hover': {
      background: 'linear-gradient(45deg, #061739, #0055A2)',
    },
  },
  successContent: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  errorContent: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

const AssignTrainingModal = ({ 
  open, 
  onClose, 
  module, 
  accessLevel, 
  selectedClient, 
  getTranslation, 
  preferredLanguage = 'english' 
}) => {
  const classes = useStyles();
  const { getAccessTokenSilently } = usePortalAuth();
  const [assignmentType, setAssignmentType] = useState('select-user');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignmentStatus, setAssignmentStatus] = useState(null); // null, 'success', 'error'

  // Fetch user list
  const { data: userData, isLoading: loadingUsers, refetch: fetchUsers } = useQuery(
    'getUserList',
    async () => {
      const token = await getAccessTokenSilently();
      return fetchApiData(basePCUrl, token, 'getUserList', accessLevel, selectedClient);
    },
    {
      enabled: false,
    }
  );

  // Handle assignment API call
  const assignTraining = async () => {
    try {
      setIsAssigning(true);
      const token = await getAccessTokenSilently();
      
      // Prepare assignment data in the same format as the original
      let assignees = [];
      
      if (assignmentType === 'assign-all') {
        assignees = userData?.users?.map(user => user.email) || [];
      } else if (assignmentType === 'select-departments') {
        // Filter users by selected departments
        assignees = userData?.users?.filter(user => 
          selectedDepartments.includes(user.department || 'Unknown')
        ).map(user => user.email) || [];
      } else {
        assignees = selectedUsers;
      }
      
      // Handle both single and multiple modules
      const modulesToAssign = Array.isArray(module) ? module : [module];
      
      const assignmentData = {
        assignees,
        courseList: modulesToAssign.map(m => ({
          trainingModule: m.modId,
          courseDesignation: 'required',
          completionDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
        }))
      };

      console.log('Assigning training:', assignmentData);
      
      const result = await postApiData(basePCUrl, token, assignmentData, 'setUserTraining');
      console.log('Assignment result:', result);
      
      setAssignmentStatus('success');
    } catch (error) {
      console.error('Assignment failed:', error);
      setAssignmentStatus('error');
    } finally {
      setIsAssigning(false);
    }
  };

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setAssignmentType('select-user');
      setSelectedUsers([]);
      setSelectedDepartments([]);
      setAssignmentStatus(null);
      setIsAssigning(false);
      fetchUsers();
    }
  }, [open, fetchUsers]);

  const handleAssignmentTypeChange = (event) => {
    setAssignmentType(event.target.value);
    if (event.target.value === 'assign-all') {
      setSelectedUsers([]);
      setSelectedDepartments([]);
    } else if (event.target.value === 'select-departments') {
      setSelectedUsers([]);
    } else if (event.target.value === 'select-user') {
      setSelectedDepartments([]);
    }
  };

  const handleUserSelection = (event) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedUsers(value);
  };

  const handleDepartmentSelection = (event) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
    setSelectedDepartments(value);
  };

  const handleAssign = () => {
    assignTraining();
  };

  const handleClose = () => {
    if (!isAssigning) {
      onClose();
    }
  };

  // Extract unique departments from user data
  const availableDepartments = React.useMemo(() => {
    if (!userData?.users) return [];
    const departments = [...new Set(userData.users.map(user => user.department || 'Unknown'))];
    return departments.sort();
  }, [userData]);

  // Count users per department
  const departmentUserCounts = React.useMemo(() => {
    if (!userData?.users) return {};
    const counts = {};
    userData.users.forEach(user => {
      const dept = user.department || 'Unknown';
      counts[dept] = (counts[dept] || 0) + 1;
    });
    return counts;
  }, [userData]);

  if (!module) return null;

  // Normalize module to array for consistent handling
  const modules = Array.isArray(module) ? module : [module];
  const isMultiple = modules.length > 1;
  const totalTime = modules.reduce((sum, m) => sum + (m.timeEstimate || 0), 0);

  // Success state
  if (assignmentStatus === 'success') {
    return (
      <Dialog open={open} onClose={handleClose} className={classes.dialog}>
        <Box className={classes.successContent}>
          <CheckCircle style={{ fontSize: 64, color: '#4caf50', marginBottom: 16 }} />
          <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 16 }}>
            {getTranslation('Training Assigned Successfully!', preferredLanguage)}
          </Typography>
          <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
            {isMultiple 
              ? `${modules.length} training modules have been assigned to the selected users.`
              : getTranslation('The training module has been assigned to the selected users.', preferredLanguage)
            }
          </Typography>
          <Button onClick={handleClose} className={classes.assignButton}>
            {getTranslation('Done', preferredLanguage)}
          </Button>
        </Box>
      </Dialog>
    );
  }

  // Error state
  if (assignmentStatus === 'error') {
    return (
      <Dialog open={open} onClose={handleClose} className={classes.dialog}>
        <Box className={classes.errorContent}>
          <Error style={{ fontSize: 64, color: '#f44336', marginBottom: 16 }} />
          <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 16 }}>
            {getTranslation('Assignment Failed', preferredLanguage)}
          </Typography>
          <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
            {getTranslation('There was an error assigning the training. Please try again.', preferredLanguage)}
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            <Button onClick={() => setAssignmentStatus(null)} color="primary">
              {getTranslation('Try Again', preferredLanguage)}
            </Button>
            <Button onClick={handleClose}>
              {getTranslation('Cancel', preferredLanguage)}
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.closeButton}>
          <Close onClick={handleClose} style={{ cursor: 'pointer' }} />
        </Box>
        
        <Box className={classes.moduleInfo}>
          {isMultiple ? (
            // Multiple modules - show stacked thumbnails
            <Box style={{ position: 'relative', width: 80, height: 80 }}>
              {modules.slice(0, 3).map((m, i) => (
                <img 
                  key={m.modId}
                  src={m.thumbnail} 
                  alt={m.title}
                  style={{
                    position: 'absolute',
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    border: '2px solid rgba(255,255,255,0.5)',
                    top: i * 8,
                    left: i * 8,
                    zIndex: 3 - i,
                    objectFit: 'cover'
                  }}
                />
              ))}
            </Box>
          ) : (
            <img 
              src={modules[0].thumbnail} 
              alt={modules[0].title}
              className={classes.thumbnail}
            />
          )}
          <Box>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 8 }}>
              {isMultiple 
                ? `Assign ${modules.length} Training Modules`
                : getTranslation('Assign Training Module', preferredLanguage)
              }
            </Typography>
            {isMultiple ? (
              <Box>
                <Typography variant="body2" style={{ opacity: 0.9, marginBottom: 4 }}>
                  {modules.slice(0, 2).map(m => m.title).join(', ')}
                  {modules.length > 2 && ` +${modules.length - 2} more`}
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.7 }}>
                  ⏱️ {Math.round(totalTime / 60)} minutes total
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" style={{ opacity: 0.9 }}>
                  {modules[0].title}
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.7, marginTop: 4 }}>
                  ⏱️ {Math.round((modules[0].timeEstimate || 0) / 60)} minutes
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <DialogContent className={classes.content}>
        {/* Assignment Type Selection */}
        <Typography className={classes.sectionTitle}>
          <Group />
          {getTranslation('Select Recipients', preferredLanguage)}
        </Typography>

        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup 
            value={assignmentType} 
            onChange={handleAssignmentTypeChange}
            style={{ gap: 8 }}
          >
            <FormControlLabel
              value="assign-all"
              control={<Radio color="primary" />}
              label={
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    🌍 {getTranslation('Assign to Everyone', preferredLanguage)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {userData?.users?.length ? 
                      `All ${userData.users.length} users will receive this training` :
                      'Loading user count...'
                    }
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="select-departments"
              control={<Radio color="primary" />}
              label={
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    🏢 {getTranslation('Assign by Department', preferredLanguage)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {availableDepartments.length ? 
                      `Choose from ${availableDepartments.length} departments` :
                      'Loading departments...'
                    }
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="select-user"
              control={<Radio color="primary" />}
              label={
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    👤 {getTranslation('Select Individual Users', preferredLanguage)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {getTranslation('Choose specific users to receive training', preferredLanguage)}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Department Selection */}
        {assignmentType === 'select-departments' && (
          <Box>
            <Divider style={{ margin: '16px 0' }} />
            <FormControl fullWidth className={classes.userSelect}>
              <InputLabel id="department-select-label">
                {getTranslation('Select Departments', preferredLanguage)}
              </InputLabel>
              <Select
                labelId="department-select-label"
                multiple
                value={selectedDepartments}
                onChange={handleDepartmentSelection}
                input={<OutlinedInput label="Select Departments" />}
                renderValue={(selected) => (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {selected.map((dept) => (
                      <Chip 
                        key={dept} 
                        label={`${dept} (${departmentUserCounts[dept] || 0})`} 
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
                disabled={loadingUsers}
              >
                {availableDepartments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box 
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: dept === 'Unknown' ? '#9e9e9e' : '#0055A2'
                        }}
                      />
                      <Typography>{dept}</Typography>
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
                        ({departmentUserCounts[dept] || 0} users)
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedDepartments.length > 0 && (
              <Box className={classes.selectedUsers}>
                <Typography variant="body2" color="textSecondary" style={{ width: '100%' }}>
                  {selectedDepartments.reduce((sum, dept) => sum + (departmentUserCounts[dept] || 0), 0)} users from {selectedDepartments.length} departments:
                </Typography>
                {selectedDepartments.map((dept) => (
                  <Chip
                    key={dept}
                    label={`${dept} (${departmentUserCounts[dept] || 0})`}
                    size="small"
                    onDelete={() => setSelectedDepartments(prev => prev.filter(d => d !== dept))}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* User Selection */}
        {assignmentType === 'select-user' && (
          <Box>
            <Divider style={{ margin: '16px 0' }} />
            <FormControl fullWidth className={classes.userSelect}>
              <InputLabel id="user-select-label">
                {getTranslation('Select Users', preferredLanguage)}
              </InputLabel>
              <Select
                labelId="user-select-label"
                multiple
                value={selectedUsers}
                onChange={handleUserSelection}
                input={<OutlinedInput label="Select Users" />}
                renderValue={(selected) => (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {selected.map((email) => (
                      <Chip key={email} label={email} size="small" />
                    ))}
                  </Box>
                )}
                disabled={loadingUsers}
              >
                {userData?.users?.map((user) => (
                  <MenuItem key={user.email} value={user.email}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar style={{ width: 24, height: 24, fontSize: '0.8rem' }}>
                        {user.email[0].toUpperCase()}
                      </Avatar>
                      {user.email}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedUsers.length > 0 && (
              <Box className={classes.selectedUsers}>
                <Typography variant="body2" color="textSecondary" style={{ width: '100%' }}>
                  {selectedUsers.length} {getTranslation('users selected', preferredLanguage)}:
                </Typography>
                {selectedUsers.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    onDelete={() => setSelectedUsers(prev => prev.filter(u => u !== email))}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 24 }}>
        <Button onClick={handleClose} disabled={isAssigning}>
          {getTranslation('Cancel', preferredLanguage)}
        </Button>
        <Button 
          onClick={handleAssign} 
          className={classes.assignButton}
          disabled={
            isAssigning || 
            (assignmentType === 'select-user' && selectedUsers.length === 0) ||
            (assignmentType === 'select-departments' && selectedDepartments.length === 0) ||
            (assignmentType === 'assign-all' && !userData?.users?.length)
          }
          startIcon={isAssigning ? <CircularProgress size={16} /> : <Assignment />}
        >
          {isAssigning 
            ? getTranslation('Assigning...', preferredLanguage)
            : getTranslation('Assign Training', preferredLanguage)
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTrainingModal;